import get from 'lodash/get';
import extend from 'lodash/extend';
import { Store } from '@ngrx/store';
import { signal } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { filter, take } from 'rxjs/operators';

import { CustomComponent, IBakery, IDeliveryOrder, IOutletDelivery } from '@cakeiteasy/web-utils';
import { CartServiceV2 } from '../cart/services/cart-v2.service';
import { DeliveryHelperService } from '../services/delivery-helper.service';
import { DeliveryApiService } from '../api-services/services/delivery-api.service';
import { IBakeryDeliveryInfo } from '../delivery/models';
import {
  CartState,
  getCart,
  getDeliveryOrder,
  IAdditionalDeliveryInfo,
  ICart,
  ICartDailyStockSoldOutProductType,
  ICartDeliveryInfo,
  ICartLine,
  IOverwritingDelivery,
  IPickupDelivery,
  UI_DELIVERY_OPTIONS
} from '../cart';
import {
  concatOutletWithDeliveryInfo,
  getDeliveryBlockedOptions,
  getInitialDeliveryType
} from '../delivery/delivery-helper';
import { UserState } from '../users/store/reducers/user.reducer';

export abstract class ChooseDeliveryBase extends CustomComponent {
  public deliveryInfo: IBakeryDeliveryInfo;

  public type: UI_DELIVERY_OPTIONS;
  public bakery: IBakery;
  public cartLines: ICartLine[];
  public outlets: IOutletDelivery[];
  public selectedOutlet = signal<IOutletDelivery | null>(null);

  /**Unconfirmed delivery-to-door / third-party (Wolt) delivery form values, lifted here so they
   * survive the child form component being destroyed and recreated when the user switches
   * delivery type and back. Only one of the two is ever rendered for a given bakery.*/
  public deliveryToDoorDraft = signal<Partial<IDeliveryOrder> | null>(null);
  public thirdPartyDeliveryDraft = signal<Partial<IDeliveryOrder> | null>(null);
  deliveryOrder: IDeliveryOrder;

  public deliverySaving$: Promise<any>;
  public baseBusy: Promise<any>;

  public UI_DELIVERY_OPTIONS: any = UI_DELIVERY_OPTIONS;

  // Products' availability
  public isPickUpAvailable: boolean;
  public isToDoorAvailable: boolean;
  public isMailToDoorAvailable: boolean;

  // Same-day availability, derived from the cart delivery info
  public isSameDayPickupAvailable = signal(false);
  public isSameDayDeliveryAvailable = signal(false);

  public availabilityErrorMessage: string;
  public blockedOptions: UI_DELIVERY_OPTIONS[];

  public outletsWithUnavailableProducts: any;

  public isCartTooCheapForDelivery: boolean;
  public overwritingDelivery: IOverwritingDelivery = null;
  public availabilityDeliveryInfo: ICartDeliveryInfo;
  public dailyStockSoldOutProductTypes: ICartDailyStockSoldOutProductType[] = [];
  public whenDeliveryInfoGotten$ = new BehaviorSubject<IBakeryDeliveryInfo>(null);
  public whenCartDeliveryInfoGotten$ = new BehaviorSubject<ICartDeliveryInfo>(null);
  public canShowDeliveryLayout = false;

  protected _defaultOptions: UI_DELIVERY_OPTIONS[] = [UI_DELIVERY_OPTIONS.PICKUP, UI_DELIVERY_OPTIONS.DELIVERY];

  protected constructor(protected _cartService: CartServiceV2,
                        protected _cartStore: Store<CartState>,
                        protected _deliveryApi: DeliveryApiService,
                        protected _deliveryHelper: DeliveryHelperService,
                        protected _userStore?: Store<UserState>) {
    super();

    this.deliverySaving$ = Promise.resolve();

    this.initDeliveryInfo();
    this._baseSubscribe();
  }

  public outletChanged(info: { outletId: number, date: Date, timeRange: string },
                       options?: { dispatchCart?: boolean, priceReducePercent?: number }): Promise<{ success?: boolean }> {
    const outlet = this.outlets.find(o => o.id === info.outletId);
    extend(outlet, { date: info.date, timeRange: info.timeRange });

    return new Promise((resolve, reject) => {
      if (info.date && info.timeRange) {
        return this._chainDeliverySaving(() => this._cartService
          .savePickup(info, options)
          .then(() => {
            this.selectedOutlet.set(outlet);
            this._cartService.setDeliveryActiveStatus(true);
            return resolve({ success: true });
          })
          .catch(() => reject())
        );
      } else {
        return resolve({ success: false });
      }
    });
  }

  public async deliveryToDoorChanged(deliveryOrder: IDeliveryOrder,
                                     options?: { dispatchCart?: boolean, priceReducePercent?: number }): Promise<{ success?: boolean }> {
    try {
      await this._cartService.saveDeliveryOrder(deliveryOrder, options);
      this._cartService.setDeliveryActiveStatus(true);
      return { success: true };
    } catch (e) {
      return { success: false };
    }
  }

  public async initOutlets() {
    const outlets = await this._getOutletsByBakeryId();
    const type = this._cartService.cart.delivery && this._cartService.cart.delivery.type;

    if (this._deliveryHelper.isPickup(type)) {
      this.outlets = concatOutletWithDeliveryInfo(outlets, this._cartService.cart.delivery);
      this.selectedOutlet.set(outlets.find(o => o.id === (this._cartService.cart.delivery as IPickupDelivery).outletId));
    } else {
      this.outlets = outlets;
      if (outlets.length === 1) {
        this.selectedOutlet.set(outlets[0]);
      } else {
        this.selectedOutlet.set(null);
      }
    }

    return this._initAvailabilityDeliveryInfo();
  }

  protected async _getOutletsByBakeryId(transformer?: (outlets: IOutletDelivery[]) => IOutletDelivery[]): Promise<IOutletDelivery[]> {
    const outlets = await this._deliveryApi.getOutletsByBakeryId(this.bakery.id, { pickupOnly: true });
    return transformer ? transformer(outlets) : outlets;
  }

  public initBlockedOptions(): void {
    this.blockedOptions = this.deliveryInfo
      ? this._getBlockedOptions(this.deliveryInfo)
      : this._defaultOptions;
  }

  protected _getBlockedOptions(deliveryInfo: IBakeryDeliveryInfo): UI_DELIVERY_OPTIONS[] {
    return getDeliveryBlockedOptions(deliveryInfo, {
      isPickUpAvailable: this.isPickUpAvailable,
      isToDoorAvailable: this.isToDoorAvailable,
      isMailToDoorAvailable: this.isMailToDoorAvailable
    });
  }

  public async initDeliveryInfo(params = {}) {
    const deliveryInfo = await this._deliveryHelper.getDeliveryInfo();
    this.deliveryInfo = { ...deliveryInfo, ...params };
    const settings = await this._makeDeliveryInfoSettings({ deliveryInfo: this.deliveryInfo });

    this._cartService.setDeliveryInfoSettings(settings);
    this.whenDeliveryInfoGotten$.next(this.deliveryInfo);
  }

  public checkCartDeliveryInfoVisibility() {
    let status = false;

    if (this._cartService.cart && this._cartService.cart.uiDeliveryType && this.type) {
      status = this.type === this._cartService.cart.uiDeliveryType;
    }
    return this._cartService.setDeliveryActiveStatus(status);
  }

  protected async _makeDeliveryInfoSettings(params: { deliveryInfo: IBakeryDeliveryInfo }): Promise<IAdditionalDeliveryInfo> {
    const {
      freeDeliveryEnabled, minCartPriceAllowsFreeDelivery, freeDeliveryAllowsOnlyForCompanies
    } = params.deliveryInfo;

    return { freeDeliveryEnabled, minCartPriceAllowsFreeDelivery, freeDeliveryAllowsOnlyForCompanies };
  }

  protected _chainDeliverySaving(nextChainPiece) {
    return this.deliverySaving$ =
      this.deliverySaving$.then(nextChainPiece.bind(this))
        // to reveal failed promise, so deliverySaving is always positive resolve even if it's fall down in the next chain piece
        .catch(() => Promise.resolve());
  }

  // Products' availability
  protected _initAvailabilityDeliveryInfo(useQueuedDeliveryInfo = true) {
    const deliveryInfoPromise = useQueuedDeliveryInfo
      ? this._cartService.getDeliveryInfo()
      : this._cartService._getDeliveryInfo();

    return Promise.all([
      deliveryInfoPromise,
      this._cartService.getDailyStockSoldOutProductTypes()
        .catch(() => []),
    ]).then(([deliveryInfo, dailyStockSoldOutProductTypes]) => {
      this.availabilityDeliveryInfo = deliveryInfo;
      this.dailyStockSoldOutProductTypes = dailyStockSoldOutProductTypes;
      this._applySameDayAvailability(deliveryInfo);
      this.whenCartDeliveryInfoGotten$.next(deliveryInfo);

      return deliveryInfo;
    });
  }

  private _applySameDayAvailability({ isSameDayPickupCart, isSameDayDeliveryCart }: ICartDeliveryInfo): void {
    this.isSameDayPickupAvailable.set(isSameDayPickupCart);
    this.isSameDayDeliveryAvailable.set(isSameDayDeliveryCart);
  }

  protected _checkOptionsAvailability() {
    this.availabilityErrorMessage = '';

    const toDoorUnavailableProducts = get(this.availabilityDeliveryInfo, 'delivery.unavailableProductTypes') || [];
    const mailToDoorUnavailableProducts = get(this.availabilityDeliveryInfo, 'mailDelivery.unavailableProductTypes') || [];

    this.outletsWithUnavailableProducts = this._deliveryHelper.getOutletsWithUnavailableProducts(this.availabilityDeliveryInfo.outlets);
    this.isPickUpAvailable = this.outlets.length !== this.outletsWithUnavailableProducts.length;
    this.isToDoorAvailable = !toDoorUnavailableProducts.length;
    this.isMailToDoorAvailable = !mailToDoorUnavailableProducts.length;

    this.initBlockedOptions();

    this.availabilityErrorMessage = this._deliveryHelper
      .getAvailabilityErrorMessage(this.deliveryInfo, toDoorUnavailableProducts, this.outletsWithUnavailableProducts, {
        isPickUpAvailable: this.isPickUpAvailable,
        isToDoorAvailable: this.isToDoorAvailable
      });
  }

  private _baseSubscribe() {
    this.addSubscriber(this._cartService.cartLinesRemovalSynced$
      .subscribe(() => this._refreshAvailabilityDeliveryInfo()));

    this.addSubscriber(this._cartStore.select(getDeliveryOrder)
      .subscribe((deliveryOrder) => this.deliveryOrder = deliveryOrder));

    this.addSubscriber(
      combineLatest(this._getSubscribeDeliverySources())
        .subscribe((params) => this._resolveDeliverySources(params)));
  }

  private async _refreshAvailabilityDeliveryInfo() {
    if (!this.availabilityDeliveryInfo) {
      return;
    }

    await (this.baseBusy = Promise.resolve(this._cartService.queueProcessing$)
      .then(() => this._initAvailabilityDeliveryInfo(false)));
  }

  protected _resolveDeliverySources(params: any[]): void {
    const [_, __, cart] = params;
    const productsPrice = this._getProductsPrice(cart);

    this.overwritingDelivery = this._getOverwritingOptions({ companyId: cart.company, productsPrice });
    this.isCartTooCheapForDelivery = this.deliveryInfo.minCartPriceAllowsDelivery > productsPrice;
    this._checkOptionsAvailability();

    setTimeout(() => {
      this.canShowDeliveryLayout = true;
    });
  }

  protected _getOverwritingOptions(params: any): IOverwritingDelivery {
    const { companyId, productsPrice } = params;
    if (this._hasFreeDelivery(companyId, productsPrice)) {
      return { price: 0 };
    }

    return null;
  }

  protected _hasFreeDelivery(companyId: number, productsPrice: number): boolean {
    const { freeDeliveryEnabled, freeDeliveryAllowsOnlyForCompanies, minCartPriceAllowsFreeDelivery } = this.deliveryInfo;

    return (!freeDeliveryAllowsOnlyForCompanies || freeDeliveryAllowsOnlyForCompanies && companyId)
      && freeDeliveryEnabled && minCartPriceAllowsFreeDelivery <= productsPrice;
  }

  protected _getSubscribeDeliverySources(): Observable<any>[] {
    return [
      this.whenDeliveryInfoGotten$.pipe(filter(a => Boolean(a))),
      this.whenCartDeliveryInfoGotten$.pipe(filter(a => Boolean(a))),
      this._cartStore.select(getCart).pipe(filter(a => Boolean(a)))
    ];
  }

  protected _getProductsPrice(cart: ICart): number {
    const deliveryPrice = cart.delivery && cart.delivery.price || 0;
    return cart.totalPrice - deliveryPrice;
  }

  protected _getInitialDeliveryType(): Promise<UI_DELIVERY_OPTIONS> {
    return new Promise(resolve => {
      combineLatest(this._getSubscribeDeliverySources())
        .pipe(take(1))
        .subscribe(([deliveryInfo, cartDeliveryInfo, { uiDeliveryType, delivery }]) => {

          const {
            outletsExists,
            mailDeliveryPointsExists,
            deliveryPointsExists,
            thirdPartyDeliveryAllowed,
          } = deliveryInfo;

          if (delivery && uiDeliveryType) {
            return resolve(uiDeliveryType);
          }

          const blockedOptions = this._getBlockedOptions(deliveryInfo);

          const { MAIL } = UI_DELIVERY_OPTIONS;

          const params = {
            outletsExists, deliveryPointsExists, thirdPartyDeliveryAllowed, isGiftDelivery: false,
            mailDeliveryPointsExists: mailDeliveryPointsExists && blockedOptions.indexOf(MAIL) === -1
          };

          resolve(getInitialDeliveryType(params, blockedOptions));
        });
    });
  }
}
